import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";
import { 
  Users, TrendingUp, TrendingDown, Calendar as CalendarIcon, 
  Phone, Mail, MapPin, Package, DollarSign, Clock, AlertTriangle,
  Plus, Search, Filter, ChevronRight, ChevronDown, RefreshCw,
  MessageSquare, PhoneCall, Video, Send, CheckCircle2, XCircle,
  Bell, Star, UserCheck, UserX, Globe, ShoppingCart, Eye, Download,
  User, Building2, Repeat, ExternalLink, Table2, CheckCheck, X,
  RotateCcw, History, Target, Award, Percent, FileText, UserPlus,
  Layers, Zap, Play, FileSpreadsheet, BookOpen
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { DMYDateInput } from "@/components/ui/dmy-date-picker";
import { formatDMY } from "@/lib/dmy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Status colors and labels
const STATUS_CONFIG = {
  active: { color: "bg-green-100 text-green-700 border-green-200", label: "Active", icon: UserCheck },
  new: { color: "bg-blue-100 text-blue-700 border-blue-200", label: "New", icon: Star },
  at_risk: { color: "bg-yellow-100 text-yellow-700 border-yellow-200", label: "At Risk", icon: AlertTriangle },
  inactive: { color: "bg-orange-100 text-orange-700 border-orange-200", label: "Inactive", icon: Clock },
  dormant: { color: "bg-red-100 text-red-700 border-red-200", label: "Dormant", icon: UserX },
  unknown: { color: "bg-gray-100 text-gray-700 border-gray-200", label: "Unknown", icon: Users }
};

const PRIORITY_CONFIG = {
  urgent: { color: "bg-red-500 text-white", label: "Urgent" },
  high: { color: "bg-orange-500 text-white", label: "High" },
  medium: { color: "bg-yellow-500 text-white", label: "Medium" },
  low: { color: "bg-green-500 text-white", label: "Low" }
};

const FOLLOW_UP_TYPES = [
  { value: "call", label: "Phone Call", icon: PhoneCall },
  { value: "email", label: "Email", icon: Mail },
  { value: "whatsapp", label: "WhatsApp", icon: MessageSquare },
  { value: "meeting", label: "Meeting", icon: Video },
  { value: "follow_up", label: "General Follow-up", icon: Bell }
];

const PIE_COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6"];

// Follow-up outcome configurations (enterprise CRM)
const OUTCOME_CONFIG = {
  connected: { label: "Connected", color: "bg-green-100 text-green-700", icon: CheckCircle2, suggestsNext: true },
  no_answer: { label: "No Answer", color: "bg-yellow-100 text-yellow-700", icon: Phone, suggestsNext: true },
  voicemail: { label: "Left Voicemail", color: "bg-blue-100 text-blue-700", icon: MessageSquare, suggestsNext: true },
  meeting_done: { label: "Meeting Done", color: "bg-purple-100 text-purple-700", icon: Video, suggestsNext: true },
  order_placed: { label: "Order Placed", color: "bg-emerald-100 text-emerald-700", icon: ShoppingCart, suggestsNext: true },
  not_interested: { label: "Not Interested", color: "bg-red-100 text-red-700", icon: XCircle, suggestsNext: false },
  wrong_number: { label: "Wrong Number", color: "bg-gray-100 text-gray-700", icon: X, suggestsNext: false },
  callback_requested: { label: "Callback Requested", color: "bg-orange-100 text-orange-700", icon: RotateCcw, suggestsNext: true }
};

const CRMPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({ search: "", country: "", status: "" });
  const [sortConfig, setSortConfig] = useState({ sort_by: "last_order", sort_order: "desc" });
  const [availableCountries, setAvailableCountries] = useState([]);
  
  // Active tab state
  const [activeTab, setActiveTab] = useState("all-customers");
  
  // All Customers tab state
  const [allCustomers, setAllCustomers] = useState([]);
  const [allCustomersLoading, setAllCustomersLoading] = useState(false);
  const [allCustomersPagination, setAllCustomersPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [allCustomersFilters, setAllCustomersFilters] = useState({ search: "", country: "", client_code: "" });
  const [allCustomersClientCodes, setAllCustomersClientCodes] = useState([]);
  const [allCustomersCountries, setAllCustomersCountries] = useState([]);
  
  // Modal states
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showClientDetail, setShowClientDetail] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showCustomerFollowUpModal, setShowCustomerFollowUpModal] = useState(false);
  const [showCustomerActivityModal, setShowCustomerActivityModal] = useState(false);
  const [clientDetail, setClientDetail] = useState(null);
  
  // Follow-up form
  const [followUpForm, setFollowUpForm] = useState({
    follow_up_date: "",
    follow_up_type: "call",
    notes: "",
    priority: "medium"
  });
  
  // Activity form
  const [activityForm, setActivityForm] = useState({
    activity_type: "call",
    description: "",
    outcome: "neutral",
    next_action: "",
    next_action_date: ""
  });

  // Customer CRM state - Partners tab (partners only)
  const [customerGroups, setCustomerGroups] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [groupCustomers, setGroupCustomers] = useState({});
  const [customerFilters, setCustomerFilters] = useState({ search: "", country: "", client_code: "" });
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerSummary, setCustomerSummary] = useState(null);
  const [availableClientCodes, setAvailableClientCodes] = useState([]);
  const [customerCountries, setCustomerCountries] = useState([]);
  
  // Clients/Agents tab state (DP Clients and Agents only)
  const [clientsAgentsGroups, setClientsAgentsGroups] = useState([]);
  const [clientsAgentsLoading, setClientsAgentsLoading] = useState(false);
  const [clientsAgentsFilters, setClientsAgentsFilters] = useState({ search: "", country: "" });
  const [clientsAgentsSummary, setClientsAgentsSummary] = useState(null);
  const [expandedClientsAgentsGroups, setExpandedClientsAgentsGroups] = useState({});
  const [clientsAgentsGroupCustomers, setClientsAgentsGroupCustomers] = useState({});

  // Follow-up Lifecycle Management (Enterprise CRM)
  const [showMarkDoneModal, setShowMarkDoneModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [selectedFollowUp, setSelectedFollowUp] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [timelineStats, setTimelineStats] = useState(null);
  const [followUpAnalytics, setFollowUpAnalytics] = useState(null);
  const [allFollowUps, setAllFollowUps] = useState([]);
  const [completedFollowUps, setCompletedFollowUps] = useState([]);
  
  // Mark Done form
  const [markDoneForm, setMarkDoneForm] = useState({
    outcome: "connected",
    notes: "",
    create_next: false,
    next_follow_up_date: "",
    next_follow_up_type: "call",
    next_follow_up_notes: "",
    next_follow_up_priority: "medium"
  });
  
  // Reschedule form
  const [rescheduleForm, setRescheduleForm] = useState({
    new_date: "",
    reason: ""
  });

  // Templates & Recurring (Phase 3)
  const [templates, setTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showTemplatesGallery, setShowTemplatesGallery] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    client_code: "",
    customer_name: "",
    start_date: new Date().toISOString().split("T")[0]
  });
  
  // Recurring Follow-up
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [recurringForm, setRecurringForm] = useState({
    client_code: "",
    customer_name: "",
    frequency: "weekly",
    follow_up_type: "call",
    notes: "",
    priority: "medium",
    start_date: new Date().toISOString().split("T")[0],
    end_date: ""
  });
  
  // Team Assignment
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignForm, setAssignForm] = useState({
    assigned_to: "",
    handoff_notes: ""
  });
  const [selectedForAssign, setSelectedForAssign] = useState(null);
  const [teamWorkload, setTeamWorkload] = useState([]);

  // Follow-up Search & Sort
  const [followUpSearch, setFollowUpSearch] = useState("");
  const [followUpSortBy, setFollowUpSortBy] = useState("date"); // date, client, priority, type
  const [followUpSortOrder, setFollowUpSortOrder] = useState("asc");
  const [followUpFilterType, setFollowUpFilterType] = useState("all"); // all, partner, client, agent

  // Analytics Search
  const [analyticsSearch, setAnalyticsSearch] = useState("");
  const [showDormantModal, setShowDormantModal] = useState(false);
  const [dormantClients, setDormantClients] = useState([]);

  // All Customers table sorting
  const [customerSortBy, setCustomerSortBy] = useState("total_orders");
  const [customerSortOrder, setCustomerSortOrder] = useState("desc");

  // Sort handler helper
  const handleTableSort = (setSortBy, setSortOrder, currentSortBy, column) => {
    if (currentSortBy === column) {
      setSortOrder(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  // Sort icon component for tables
  const TableSortIcon = ({ sortBy, sortOrder, column }) => {
    if (sortBy !== column) {
      return <span className="ml-1 text-slate-300">⇅</span>;
    }
    return <span className="ml-1 text-blue-600">{sortOrder === "asc" ? "↑" : "↓"}</span>;
  };

  // Fetch clients
  const fetchClients = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        sort_by: sortConfig.sort_by,
        sort_order: sortConfig.sort_order
      });
      
      if (filters.search) params.append("search", filters.search);
      if (filters.country) params.append("country", filters.country);
      if (filters.status) params.append("status", filters.status);
      
      const response = await fetch(`${BACKEND_URL}/api/crm/clients?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setClients(data.clients);
        setPagination(prev => ({ ...prev, total: data.total, pages: data.pages }));
        setSummary(data.summary);
        setAvailableCountries(data.filters?.countries || []);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Failed to fetch clients");
    } finally {
      setLoading(false);
    }
  };

  // Fetch dashboard
  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    }
  };

  // Fetch customer groups (grouped by client code) - PARTNERS ONLY
  const fetchCustomerGroups = async () => {
    setCustomerLoading(true);
    try {
      const params = new URLSearchParams();
      if (customerFilters.search) params.append("search", customerFilters.search);
      if (customerFilters.country) params.append("country", customerFilters.country);
      params.append("role_filter", "partners_only"); // Only show Partners
      
      const response = await fetch(`${BACKEND_URL}/api/crm/customers/by-client?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCustomerGroups(data.groups || []);
        setCustomerSummary({
          total_groups: data.total_groups,
          total_customers: data.total_customers
        });
        
        // Get unique countries from groups
        const allCountries = new Set();
        data.groups?.forEach(g => g.countries?.forEach(c => allCountries.add(c)));
        setCustomerCountries(Array.from(allCountries).sort());
        setAvailableClientCodes(data.groups?.map(g => g.client_code) || []);
      }
    } catch (error) {
      console.error("Error fetching customer groups:", error);
      toast.error("Failed to fetch partner data");
    } finally {
      setCustomerLoading(false);
    }
  };

  // Fetch Clients/Agents groups (DP Clients and Agents only)
  const fetchClientsAgentsGroups = async () => {
    setClientsAgentsLoading(true);
    try {
      const params = new URLSearchParams();
      if (clientsAgentsFilters.search) params.append("search", clientsAgentsFilters.search);
      if (clientsAgentsFilters.country) params.append("country", clientsAgentsFilters.country);
      params.append("role_filter", "clients_agents_only"); // Only show DP Clients and Agents
      
      const response = await fetch(`${BACKEND_URL}/api/crm/customers/by-client?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setClientsAgentsGroups(data.groups || []);
        setClientsAgentsSummary({
          total_groups: data.total_groups,
          total_customers: data.total_customers
        });
      }
    } catch (error) {
      console.error("Error fetching clients/agents groups:", error);
      toast.error("Failed to fetch clients/agents data");
    } finally {
      setClientsAgentsLoading(false);
    }
  };

  // Fetch customers for Clients/Agents tab (on expand)
  const fetchCustomersForClientsAgents = async (clientCode) => {
    try {
      const params = new URLSearchParams({ client_code: clientCode, limit: 500 });
      if (clientsAgentsFilters.country) params.append("country", clientsAgentsFilters.country);
      
      const response = await fetch(`${BACKEND_URL}/api/crm/customers?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setClientsAgentsGroupCustomers(prev => ({ ...prev, [clientCode]: data.customers || [] }));
      }
    } catch (error) {
      console.error("Error fetching customers for client:", error);
    }
  };

  // Toggle group expansion for Clients/Agents tab
  const toggleClientsAgentsGroup = (clientCode) => {
    setExpandedClientsAgentsGroups(prev => {
      const newState = { ...prev, [clientCode]: !prev[clientCode] };
      
      // Fetch customers if expanding and not already loaded
      if (newState[clientCode] && !clientsAgentsGroupCustomers[clientCode]) {
        fetchCustomersForClientsAgents(clientCode);
      }
      
      return newState;
    });
  };

  // Fetch customers for a specific client code (on expand)
  const fetchCustomersForClient = async (clientCode) => {
    try {
      const params = new URLSearchParams({ client_code: clientCode, limit: 500 });
      if (customerFilters.country) params.append("country", customerFilters.country);
      
      const response = await fetch(`${BACKEND_URL}/api/crm/customers?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setGroupCustomers(prev => ({ ...prev, [clientCode]: data.customers || [] }));
      }
    } catch (error) {
      console.error("Error fetching customers for client:", error);
    }
  };

  // Toggle group expansion
  const toggleGroup = (clientCode) => {
    setExpandedGroups(prev => {
      const newState = { ...prev, [clientCode]: !prev[clientCode] };
      
      // Fetch customers if expanding and not already loaded
      if (newState[clientCode] && !groupCustomers[clientCode]) {
        fetchCustomersForClient(clientCode);
      }
      
      return newState;
    });
  };

  // Open customer report in new tab
  const handleOpenReport = (clientCode) => {
    window.open(`/crm/customer-report/${clientCode}`, '_blank');
  };

  // Open timeline in new tab
  const handleOpenTimeline = (clientCode) => {
    window.open(`/client-timeline/${clientCode}`, '_blank');
  };

  // Fetch ALL customers for flat table
  const fetchAllCustomers = async () => {
    setAllCustomersLoading(true);
    try {
      const params = new URLSearchParams({
        page: allCustomersPagination.page,
        limit: allCustomersPagination.limit
      });
      
      if (allCustomersFilters.search) params.append("search", allCustomersFilters.search);
      if (allCustomersFilters.country) params.append("country", allCustomersFilters.country);
      if (allCustomersFilters.client_code) params.append("client_code", allCustomersFilters.client_code);
      
      const response = await fetch(`${BACKEND_URL}/api/crm/customers?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAllCustomers(data.customers || []);
        setAllCustomersPagination(prev => ({ ...prev, total: data.total, pages: data.pages }));
        setAllCustomersClientCodes(data.filters?.client_codes || []);
        setAllCustomersCountries(data.filters?.countries || []);
      }
    } catch (error) {
      console.error("Error fetching all customers:", error);
      toast.error("Failed to fetch customers");
    } finally {
      setAllCustomersLoading(false);
    }
  };

  // Fetch client detail
  const fetchClientDetail = async (clientCode) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/client/${clientCode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setClientDetail(data);
        setShowClientDetail(true);
      }
    } catch (error) {
      console.error("Error fetching client detail:", error);
      toast.error("Failed to fetch client details");
    }
  };

  // Create follow-up
  const handleCreateFollowUp = async () => {
    if (!selectedClient || !followUpForm.follow_up_date) {
      toast.error("Please fill all required fields");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/follow-up`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          client_code: selectedClient.client_code,
          ...followUpForm
        })
      });
      
      if (response.ok) {
        toast.success("Follow-up scheduled successfully");
        setShowFollowUpModal(false);
        setFollowUpForm({ follow_up_date: "", follow_up_type: "call", notes: "", priority: "medium" });
        fetchClients();
        fetchDashboard();
      } else {
        toast.error("Failed to create follow-up");
      }
    } catch (error) {
      toast.error("Failed to create follow-up");
    }
  };

  // Log activity
  const handleLogActivity = async () => {
    if (!selectedClient || !activityForm.description) {
      toast.error("Please fill all required fields");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/activity`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          client_code: selectedClient.client_code,
          ...activityForm
        })
      });
      
      if (response.ok) {
        toast.success("Activity logged successfully");
        setShowActivityModal(false);
        setActivityForm({ activity_type: "call", description: "", outcome: "neutral", next_action: "", next_action_date: "" });
        fetchDashboard();
        if (showClientDetail) {
          fetchClientDetail(selectedClient.client_code);
        }
      } else {
        toast.error("Failed to log activity");
      }
    } catch (error) {
      toast.error("Failed to log activity");
    }
  };

  // Create follow-up for CUSTOMER (from All Customers tab)
  const handleCreateCustomerFollowUp = async () => {
    if (!selectedCustomer || !followUpForm.follow_up_date) {
      toast.error("Please fill all required fields");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/follow-up`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          client_code: selectedCustomer.client_code,
          customer_name: selectedCustomer.customer_name,
          ...followUpForm
        })
      });
      
      if (response.ok) {
        toast.success("Follow-up scheduled successfully");
        setShowCustomerFollowUpModal(false);
        setFollowUpForm({ follow_up_date: "", follow_up_type: "call", notes: "", priority: "medium" });
        fetchDashboard();
      } else {
        toast.error("Failed to create follow-up");
      }
    } catch (error) {
      toast.error("Failed to create follow-up");
    }
  };

  // Log activity for CUSTOMER
  const handleLogCustomerActivity = async () => {
    if (!selectedCustomer || !activityForm.description) {
      toast.error("Please fill all required fields");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/activity`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          client_code: selectedCustomer.client_code,
          customer_name: selectedCustomer.customer_name,
          ...activityForm
        })
      });
      
      if (response.ok) {
        toast.success("Activity logged successfully");
        setShowCustomerActivityModal(false);
        setActivityForm({ activity_type: "call", description: "", outcome: "neutral", next_action: "", next_action_date: "" });
        fetchDashboard();
      } else {
        toast.error("Failed to log activity");
      }
    } catch (error) {
      toast.error("Failed to log activity");
    }
  };

  // ==================== FOLLOW-UP LIFECYCLE MANAGEMENT ====================
  
  // Fetch all follow-ups for the Follow-ups tab
  const fetchAllFollowUps = async () => {
    try {
      const [pendingRes, completedRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/crm/follow-ups?status=pending`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/crm/follow-ups?status=completed`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setAllFollowUps(data.follow_ups || []);
      }
      if (completedRes.ok) {
        const data = await completedRes.json();
        setCompletedFollowUps(data.follow_ups?.slice(0, 20) || []);
      }
    } catch (error) {
      console.error("Error fetching follow-ups:", error);
    }
  };

  // Fetch follow-up analytics
  const fetchFollowUpAnalytics = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/analytics/follow-ups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFollowUpAnalytics(data);
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
    }
  };

  // Fetch client/customer timeline
  const fetchTimeline = async (clientCode) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/client/${clientCode}/timeline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTimeline(data.timeline || []);
        setTimelineStats(data.stats);
      }
    } catch (error) {
      console.error("Error fetching timeline:", error);
      toast.error("Failed to load timeline");
    }
  };

  // Mark follow-up as done
  const handleMarkDone = async (e) => {
    e.preventDefault();
    if (!selectedFollowUp) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/follow-up/${selectedFollowUp.id}/complete`, {
        method: "PUT",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(markDoneForm)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "Follow-up completed!");
        setShowMarkDoneModal(false);
        setSelectedFollowUp(null);
        setMarkDoneForm({
          outcome: "connected",
          notes: "",
          create_next: false,
          next_follow_up_date: "",
          next_follow_up_type: "call",
          next_follow_up_notes: "",
          next_follow_up_priority: "medium"
        });
        // Refresh data
        fetchDashboard();
        fetchAllFollowUps();
      } else {
        toast.error("Failed to complete follow-up");
      }
    } catch (error) {
      toast.error("Failed to complete follow-up");
    }
  };

  // Reschedule follow-up
  const handleReschedule = async (e) => {
    e.preventDefault();
    if (!selectedFollowUp || !rescheduleForm.new_date) {
      toast.error("Please select a new date");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/follow-up/${selectedFollowUp.id}/reschedule`, {
        method: "PUT",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(rescheduleForm)
      });
      
      if (response.ok) {
        toast.success("Follow-up rescheduled!");
        setShowRescheduleModal(false);
        setSelectedFollowUp(null);
        setRescheduleForm({ new_date: "", reason: "" });
        fetchDashboard();
        fetchAllFollowUps();
      } else {
        toast.error("Failed to reschedule");
      }
    } catch (error) {
      toast.error("Failed to reschedule");
    }
  };

  // Cancel follow-up
  const handleCancelFollowUp = async (followUp, reason = "") => {
    if (!window.confirm("Are you sure you want to cancel this follow-up?")) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/follow-up/${followUp.id}/cancel`, {
        method: "PUT",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ reason })
      });
      
      if (response.ok) {
        toast.success("Follow-up cancelled");
        fetchDashboard();
        fetchAllFollowUps();
      } else {
        toast.error("Failed to cancel");
      }
    } catch (error) {
      toast.error("Failed to cancel");
    }
  };

  // Open Mark Done modal
  const openMarkDone = (followUp) => {
    setSelectedFollowUp(followUp);
    setMarkDoneForm({
      outcome: "connected",
      notes: "",
      create_next: false,
      next_follow_up_date: "",
      next_follow_up_type: "call",
      next_follow_up_notes: "",
      next_follow_up_priority: "medium"
    });
    setShowMarkDoneModal(true);
  };

  // Open Reschedule modal
  const openReschedule = (followUp) => {
    setSelectedFollowUp(followUp);
    setRescheduleForm({ new_date: "", reason: "" });
    setShowRescheduleModal(true);
  };

  // Open Timeline page (dedicated page instead of modal)
  const openTimeline = (clientCode) => {
    navigate(`/crm/client/${clientCode}/timeline`);
  };

  // Open Timeline modal (for quick view)
  const openTimelineModal = (clientCode) => {
    fetchTimeline(clientCode);
    setShowTimelineModal(true);
  };

  // ==================== TEMPLATES & RECURRING (Phase 3) ====================
  
  // Fetch templates
  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  // Apply template
  const handleApplyTemplate = async (e) => {
    e.preventDefault();
    if (!selectedTemplate || !templateForm.client_code) {
      toast.error("Please select a template and client");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/templates/${selectedTemplate.id}/apply`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(templateForm)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowTemplateModal(false);
        setSelectedTemplate(null);
        setTemplateForm({ client_code: "", customer_name: "", start_date: new Date().toISOString().split("T")[0] });
        fetchDashboard();
        fetchAllFollowUps();
      } else {
        toast.error("Failed to apply template");
      }
    } catch (error) {
      toast.error("Failed to apply template");
    }
  };

  // Create recurring follow-up
  const handleCreateRecurring = async (e) => {
    e.preventDefault();
    if (!recurringForm.client_code || !recurringForm.start_date) {
      toast.error("Client code and start date are required");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/recurring-follow-up`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(recurringForm)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowRecurringModal(false);
        setRecurringForm({
          client_code: "", customer_name: "", frequency: "weekly",
          follow_up_type: "call", notes: "", priority: "medium",
          start_date: new Date().toISOString().split("T")[0], end_date: ""
        });
        fetchDashboard();
        fetchAllFollowUps();
      } else {
        toast.error("Failed to create recurring follow-up");
      }
    } catch (error) {
      toast.error("Failed to create recurring follow-up");
    }
  };

  // Download CRM Excel Templates for Partners
  const downloadCRMTemplate = (templateType) => {
    const wb = XLSX.utils.book_new();
    
    if (templateType === "customer_tracker") {
      // Customer Tracker Template
      const wsData = [
        ["CUSTOMER TRACKER TEMPLATE"],
        ["Instructions: Fill in your customer details. This template helps track customer interactions and orders."],
        [],
        ["Customer Name", "Country", "Contact Number", "Email", "First Order Date", "Last Order Date", "Total Orders", "Total Value (₹)", "Status", "Notes", "Next Follow-up Date"],
        ["John Smith", "USA", "+1-555-1234", "john@email.com", "2025-01-15", "2025-06-10", "5", "25000", "Active", "Prefers email communication", "2025-07-01"],
        ["Maria Garcia", "Spain", "+34-555-5678", "maria@email.com", "2025-03-20", "2025-05-25", "3", "15000", "Active", "VIP customer", "2025-06-15"],
        ["", "", "", "", "", "", "", "", "", "", ""],
        ["", "", "", "", "", "", "", "", "", "", ""],
      ];
      
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws["!cols"] = [
        { wch: 20 }, { wch: 12 }, { wch: 15 }, { wch: 25 }, 
        { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 15 },
        { wch: 12 }, { wch: 30 }, { wch: 18 }
      ];
      XLSX.utils.book_append_sheet(wb, ws, "Customer Tracker");
      
    } else if (templateType === "follow_up_log") {
      // Follow-up Log Template
      const wsData = [
        ["FOLLOW-UP LOG TEMPLATE"],
        ["Instructions: Record all customer interactions. Keep this updated for better relationship management."],
        [],
        ["Date", "Customer Name", "Client Code", "Follow-up Type", "Priority", "Notes/Discussion", "Outcome", "Next Action", "Next Follow-up Date", "Assigned To"],
        ["2025-06-01", "John Smith", "AAA", "Call", "High", "Discussed new order requirements", "Successful - Order placed", "Send invoice", "2025-06-15", "Sales Team"],
        ["2025-06-02", "Maria Garcia", "BBB", "Email", "Medium", "Sent product catalog", "Awaiting response", "Follow-up call", "2025-06-09", "Sales Team"],
        ["", "", "", "", "", "", "", "", "", ""],
      ];
      
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws["!cols"] = [
        { wch: 12 }, { wch: 20 }, { wch: 12 }, { wch: 15 }, 
        { wch: 10 }, { wch: 35 }, { wch: 25 }, { wch: 20 },
        { wch: 18 }, { wch: 15 }
      ];
      XLSX.utils.book_append_sheet(wb, ws, "Follow-up Log");
      
    } else if (templateType === "order_tracker") {
      // Order Tracker Template
      const wsData = [
        ["ORDER TRACKER TEMPLATE"],
        ["Instructions: Track all orders from customers. Update status as orders progress."],
        [],
        ["Order Date", "Customer Name", "Country", "Tracking Number", "Products", "Quantity", "Order Value (₹)", "Shipping Cost (₹)", "Total (₹)", "Status", "Delivery Date", "Notes"],
        ["2025-06-01", "John Smith", "USA", "EE123456789IN", "Product A, Product B", "10, 5", "15000", "2500", "17500", "Shipped", "2025-06-15", "Express shipping"],
        ["2025-06-02", "Maria Garcia", "Spain", "RR987654321IN", "Product C", "20", "8000", "1500", "9500", "In Transit", "", "Standard shipping"],
        ["", "", "", "", "", "", "", "", "", "", "", ""],
      ];
      
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws["!cols"] = [
        { wch: 12 }, { wch: 20 }, { wch: 12 }, { wch: 18 }, 
        { wch: 25 }, { wch: 12 }, { wch: 15 }, { wch: 15 },
        { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 25 }
      ];
      XLSX.utils.book_append_sheet(wb, ws, "Order Tracker");
      
    } else if (templateType === "payment_tracker") {
      // Payment Tracker Template
      const wsData = [
        ["PAYMENT TRACKER TEMPLATE"],
        ["Instructions: Track payments received and pending from customers."],
        [],
        ["Invoice Date", "Customer Name", "Client Code", "Invoice Number", "Invoice Amount (₹)", "Payment Received (₹)", "Balance Due (₹)", "Due Date", "Payment Status", "Payment Method", "Notes"],
        ["2025-06-01", "John Smith", "AAA", "INV-2025-001", "25000", "25000", "0", "2025-06-15", "Paid", "Bank Transfer", "Paid on time"],
        ["2025-06-02", "Maria Garcia", "BBB", "INV-2025-002", "15000", "10000", "5000", "2025-06-20", "Partial", "UPI", "Remaining due next week"],
        ["", "", "", "", "", "", "", "", "", "", ""],
      ];
      
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws["!cols"] = [
        { wch: 12 }, { wch: 20 }, { wch: 12 }, { wch: 15 }, 
        { wch: 18 }, { wch: 18 }, { wch: 15 }, { wch: 12 },
        { wch: 15 }, { wch: 15 }, { wch: 25 }
      ];
      XLSX.utils.book_append_sheet(wb, ws, "Payment Tracker");
      
    } else if (templateType === "complete_crm") {
      // Complete CRM Package - All sheets in one workbook
      
      // Sheet 1: Customer Tracker
      const customerData = [
        ["CUSTOMER TRACKER"],
        ["Customer Name", "Country", "Contact Number", "Email", "First Order Date", "Last Order Date", "Total Orders", "Total Value (₹)", "Status", "Notes"],
        ["", "", "", "", "", "", "", "", "", ""],
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(customerData);
      ws1["!cols"] = [{ wch: 20 }, { wch: 12 }, { wch: 15 }, { wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 15 }, { wch: 12 }, { wch: 30 }];
      XLSX.utils.book_append_sheet(wb, ws1, "Customers");
      
      // Sheet 2: Follow-up Log
      const followUpData = [
        ["FOLLOW-UP LOG"],
        ["Date", "Customer Name", "Type", "Priority", "Notes", "Outcome", "Next Action", "Next Date"],
        ["", "", "", "", "", "", "", ""],
      ];
      const ws2 = XLSX.utils.aoa_to_sheet(followUpData);
      ws2["!cols"] = [{ wch: 12 }, { wch: 20 }, { wch: 12 }, { wch: 10 }, { wch: 35 }, { wch: 25 }, { wch: 20 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(wb, ws2, "Follow-ups");
      
      // Sheet 3: Orders
      const orderData = [
        ["ORDER TRACKER"],
        ["Date", "Customer", "Country", "Tracking", "Products", "Qty", "Value (₹)", "Shipping (₹)", "Status"],
        ["", "", "", "", "", "", "", "", ""],
      ];
      const ws3 = XLSX.utils.aoa_to_sheet(orderData);
      ws3["!cols"] = [{ wch: 12 }, { wch: 20 }, { wch: 12 }, { wch: 18 }, { wch: 25 }, { wch: 8 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(wb, ws3, "Orders");
      
      // Sheet 4: Payments
      const paymentData = [
        ["PAYMENT TRACKER"],
        ["Date", "Customer", "Invoice #", "Amount (₹)", "Received (₹)", "Balance (₹)", "Due Date", "Status"],
        ["", "", "", "", "", "", "", ""],
      ];
      const ws4 = XLSX.utils.aoa_to_sheet(paymentData);
      ws4["!cols"] = [{ wch: 12 }, { wch: 20 }, { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(wb, ws4, "Payments");
    }
    
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    
    const filename = `CRM_Template_${templateType}_${new Date().toISOString().split('T')[0]}.xlsx`;
    saveAs(blob, filename);
    
    toast.success(`Downloaded ${templateType.replace(/_/g, ' ')} template`);
  };

  // Fetch team workload
  const fetchTeamWorkload = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/team-workload`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTeamWorkload(data.workload || []);
      }
    } catch (error) {
      console.error("Error fetching team workload:", error);
    }
  };

  // Assign follow-up
  const handleAssignFollowUp = async (e) => {
    e.preventDefault();
    if (!selectedForAssign || !assignForm.assigned_to) {
      toast.error("Please select a team member");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/follow-up/${selectedForAssign.id}/assign`, {
        method: "PUT",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(assignForm)
      });
      
      if (response.ok) {
        toast.success(`Follow-up assigned to ${assignForm.assigned_to}`);
        setShowAssignModal(false);
        setSelectedForAssign(null);
        setAssignForm({ assigned_to: "", handoff_notes: "" });
        fetchAllFollowUps();
        fetchTeamWorkload();
      } else {
        toast.error("Failed to assign follow-up");
      }
    } catch (error) {
      toast.error("Failed to assign follow-up");
    }
  };

  // Open template modal with client prefilled
  const openTemplateModalForClient = (clientCode, customerName = "") => {
    setTemplateForm({ client_code: clientCode, customer_name: customerName, start_date: new Date().toISOString().split("T")[0] });
    fetchTemplates();
    setShowTemplateModal(true);
  };

  // Open recurring modal with client prefilled
  const openRecurringModalForClient = (clientCode, customerName = "") => {
    setRecurringForm({
      client_code: clientCode, customer_name: customerName, frequency: "weekly",
      follow_up_type: "call", notes: "", priority: "medium",
      start_date: new Date().toISOString().split("T")[0], end_date: ""
    });
    setShowRecurringModal(true);
  };

  // Open assign modal
  const openAssignModal = (followUp) => {
    setSelectedForAssign(followUp);
    setAssignForm({ assigned_to: followUp.assigned_to || "", handoff_notes: "" });
    fetchTeamWorkload();
    setShowAssignModal(true);
  };

  useEffect(() => {
    fetchClients();
    fetchDashboard();
    fetchCustomerGroups();
    fetchClientsAgentsGroups();
    fetchAllCustomers();
  }, [pagination.page, sortConfig, filters.status, filters.country]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (pagination.page === 1) {
        fetchClients();
      } else {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Debounced customer search (Partners tab)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCustomerGroups();
    }, 500);
    return () => clearTimeout(timer);
  }, [customerFilters.search, customerFilters.country]);

  // Debounced clients/agents search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchClientsAgentsGroups();
    }, 500);
    return () => clearTimeout(timer);
  }, [clientsAgentsFilters.search, clientsAgentsFilters.country]);

  // Debounced all customers search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (allCustomersPagination.page === 1) {
        fetchAllCustomers();
      } else {
        setAllCustomersPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [allCustomersFilters.search, allCustomersFilters.country, allCustomersFilters.client_code]);

  // All customers pagination
  useEffect(() => {
    fetchAllCustomers();
  }, [allCustomersPagination.page]);

  // Fetch follow-ups and analytics when tab is active
  useEffect(() => {
    if (activeTab === "follow-ups") {
      fetchAllFollowUps();
    }
    if (activeTab === "analytics") {
      fetchFollowUpAnalytics();
    }
  }, [activeTab]);

  // Format currency
  const formatCurrency = (val) => {
    if (!val) return "₹0";
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(val);
  };

  // Pie chart data for client status
  const statusPieData = useMemo(() => {
    if (!summary) return [];
    return [
      { name: "Active", value: summary.active, color: "#10b981" },
      { name: "New", value: summary.new, color: "#3b82f6" },
      { name: "At Risk", value: summary.at_risk, color: "#f59e0b" },
      { name: "Dormant", value: summary.dormant, color: "#ef4444" }
    ].filter(d => d.value > 0);
  }, [summary]);

  // Filtered and sorted follow-ups
  const filteredSortedFollowUps = useMemo(() => {
    let result = [...allFollowUps];
    
    // Apply search filter
    if (followUpSearch) {
      const search = followUpSearch.toLowerCase();
      result = result.filter(f => 
        f.client_code?.toLowerCase().includes(search) ||
        f.customer_name?.toLowerCase().includes(search) ||
        f.notes?.toLowerCase().includes(search) ||
        f.assigned_to?.toLowerCase().includes(search)
      );
    }
    
    // Apply type filter (partner/client/agent)
    if (followUpFilterType !== "all") {
      // Would need role info from ledgers - for now filter by naming convention
      result = result.filter(f => {
        if (followUpFilterType === "client") {
          // DP Clients usually have specific codes
          return f.client_code;
        }
        return true;
      });
    }
    
    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;
      switch (followUpSortBy) {
        case "date":
          comparison = (a.follow_up_date || "").localeCompare(b.follow_up_date || "");
          break;
        case "client":
          comparison = (a.client_code || "").localeCompare(b.client_code || "");
          break;
        case "priority":
          const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
          comparison = (priorityOrder[a.priority] || 3) - (priorityOrder[b.priority] || 3);
          break;
        case "type":
          comparison = (a.follow_up_type || "").localeCompare(b.follow_up_type || "");
          break;
        default:
          break;
      }
      return followUpSortOrder === "asc" ? comparison : -comparison;
    });
    
    return result;
  }, [allFollowUps, followUpSearch, followUpFilterType, followUpSortBy, followUpSortOrder]);

  // Sorted all customers
  const sortedAllCustomers = useMemo(() => {
    if (!allCustomers || allCustomers.length === 0) return [];
    
    const sorted = [...allCustomers];
    sorted.sort((a, b) => {
      let comparison = 0;
      switch (customerSortBy) {
        case "customer_name":
          comparison = (a.customer_name || "").localeCompare(b.customer_name || "");
          break;
        case "client_code":
          comparison = (a.client_code || "").localeCompare(b.client_code || "");
          break;
        case "country":
          comparison = (a.country || "").localeCompare(b.country || "");
          break;
        case "total_orders":
          comparison = (a.total_orders || 0) - (b.total_orders || 0);
          break;
        case "total_value":
          comparison = (a.total_value || 0) - (b.total_value || 0);
          break;
        case "last_order":
          comparison = (a.last_order || "").localeCompare(b.last_order || "");
          break;
        default:
          break;
      }
      return customerSortOrder === "asc" ? comparison : -comparison;
    });
    
    return sorted;
  }, [allCustomers, customerSortBy, customerSortOrder]);

  // Fetch dormant clients
  const fetchDormantClients = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/clients?status=dormant&limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDormantClients(data.clients || []);
        setShowDormantModal(true);
      }
    } catch (error) {
      console.error("Error fetching dormant clients:", error);
      toast.error("Failed to fetch dormant clients");
    }
  };

  return (
    <div className="p-6 space-y-6 bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Users className="w-7 h-7 text-blue-600" />
            Client Intelligence Hub
          </h1>
          <p className="text-slate-500 text-sm mt-1">CRM Dashboard - Track, Engage & Grow Your Clients</p>
        </div>
        <Button onClick={() => { fetchClients(); fetchDashboard(); }} variant="outline" size="sm">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Dynamic Dashboard Cards - Change based on active tab */}
      {activeTab === "all-customers" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-indigo-100 text-sm">Total Customers</p>
                  <p className="text-3xl font-bold">{allCustomersPagination.total || 0}</p>
                </div>
                <User className="w-10 h-10 text-indigo-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-teal-100 text-sm">Partner Groups</p>
                  <p className="text-3xl font-bold">{allCustomersClientCodes.length || 0}</p>
                </div>
                <Building2 className="w-10 h-10 text-teal-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm">Countries</p>
                  <p className="text-3xl font-bold">{allCustomersCountries.length || 0}</p>
                </div>
                <Globe className="w-10 h-10 text-amber-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Follow-ups Today</p>
                  <p className="text-3xl font-bold">{dashboard?.follow_ups?.due_today || 0}</p>
                </div>
                <Bell className="w-10 h-10 text-purple-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === "customers" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-indigo-100 text-sm">Total Customers</p>
                  <p className="text-3xl font-bold">{customerSummary?.total_customers || 0}</p>
                </div>
                <User className="w-10 h-10 text-indigo-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-teal-100 text-sm">Partner Groups</p>
                  <p className="text-3xl font-bold">{customerSummary?.total_groups || 0}</p>
                </div>
                <Building2 className="w-10 h-10 text-teal-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm">Countries</p>
                  <p className="text-3xl font-bold">{customerCountries.length || 0}</p>
                </div>
                <Globe className="w-10 h-10 text-amber-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Follow-ups Today</p>
                  <p className="text-3xl font-bold">{dashboard?.follow_ups?.due_today || 0}</p>
                </div>
                <Bell className="w-10 h-10 text-purple-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {(activeTab === "clients" || activeTab === "follow-ups" || activeTab === "analytics") && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Total Partners</p>
                  <p className="text-3xl font-bold">{summary?.total_clients || 0}</p>
                </div>
                <Building2 className="w-10 h-10 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Active (30 days)</p>
                  <p className="text-3xl font-bold">{summary?.active || 0}</p>
                </div>
                <UserCheck className="w-10 h-10 text-green-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-yellow-100 text-sm">At Risk</p>
                  <p className="text-3xl font-bold">{summary?.at_risk || 0}</p>
                </div>
                <AlertTriangle className="w-10 h-10 text-yellow-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Follow-ups Today</p>
                  <p className="text-3xl font-bold">{dashboard?.follow_ups?.due_today || 0}</p>
                  {dashboard?.follow_ups?.overdue > 0 && (
                    <p className="text-xs text-red-200 mt-1">+{dashboard.follow_ups.overdue} overdue</p>
                  )}
                </div>
                <Bell className="w-10 h-10 text-purple-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-cyan-100 text-sm">Dormant</p>
                  <p className="text-3xl font-bold">{summary?.dormant || 0}</p>
                </div>
                <UserX className="w-10 h-10 text-cyan-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white shadow-sm">
          <TabsTrigger value="all-customers" className="gap-2">
            <Table2 className="w-4 h-4" /> All Customers
          </TabsTrigger>
          <TabsTrigger value="customers" className="gap-2">
            <Building2 className="w-4 h-4" /> By Partner
          </TabsTrigger>
          <TabsTrigger value="clients" className="gap-2">
            <Users className="w-4 h-4" /> Clients/Agents
          </TabsTrigger>
          <TabsTrigger value="follow-ups" className="gap-2">
            <Bell className="w-4 h-4" /> Follow-ups
          </TabsTrigger>
          <TabsTrigger value="analytics" className="gap-2">
            <TrendingUp className="w-4 h-4" /> Analytics
          </TabsTrigger>
        </TabsList>

        {/* ALL CUSTOMERS Tab (NEW - Simple Flat Table) */}
        <TabsContent value="all-customers" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-xs text-slate-500">Search Customer</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search customer name..."
                      value={allCustomersFilters.search}
                      onChange={(e) => setAllCustomersFilters(prev => ({ ...prev, search: e.target.value }))}
                      className="pl-9"
                    />
                  </div>
                </div>
                
                <div className="w-40">
                  <Label className="text-xs text-slate-500">Partner/Client</Label>
                  <Select 
                    value={allCustomersFilters.client_code} 
                    onValueChange={(v) => setAllCustomersFilters(prev => ({ ...prev, client_code: v === "all" ? "" : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Partners" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Partners</SelectItem>
                      {allCustomersClientCodes.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="w-40">
                  <Label className="text-xs text-slate-500">Country</Label>
                  <Select 
                    value={allCustomersFilters.country} 
                    onValueChange={(v) => setAllCustomersFilters(prev => ({ ...prev, country: v === "all" ? "" : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Countries" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Countries</SelectItem>
                      {allCustomersCountries.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchAllCustomers}
                  disabled={allCustomersLoading}
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${allCustomersLoading ? "animate-spin" : ""}`} />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Customer Table */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  Customer Database 
                  <span className="text-sm font-normal text-slate-500 ml-2">
                    ({allCustomersPagination.total} customers)
                  </span>
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="all-customers-table">
                  <thead>
                    <tr className="border-b bg-slate-100">
                      <th 
                        className="text-left p-3 font-medium cursor-pointer hover:bg-slate-200 select-none"
                        onClick={() => handleTableSort(setCustomerSortBy, setCustomerSortOrder, customerSortBy, "client_code")}
                      >
                        Partner Code <TableSortIcon sortBy={customerSortBy} sortOrder={customerSortOrder} column="client_code" />
                      </th>
                      <th 
                        className="text-left p-3 font-medium cursor-pointer hover:bg-slate-200 select-none"
                        onClick={() => handleTableSort(setCustomerSortBy, setCustomerSortOrder, customerSortBy, "customer_name")}
                      >
                        Customer Name <TableSortIcon sortBy={customerSortBy} sortOrder={customerSortOrder} column="customer_name" />
                      </th>
                      <th 
                        className="text-left p-3 font-medium cursor-pointer hover:bg-slate-200 select-none"
                        onClick={() => handleTableSort(setCustomerSortBy, setCustomerSortOrder, customerSortBy, "country")}
                      >
                        Country <TableSortIcon sortBy={customerSortBy} sortOrder={customerSortOrder} column="country" />
                      </th>
                      <th 
                        className="text-center p-3 font-medium cursor-pointer hover:bg-slate-200 select-none"
                        onClick={() => handleTableSort(setCustomerSortBy, setCustomerSortOrder, customerSortBy, "total_orders")}
                      >
                        Total Orders <TableSortIcon sortBy={customerSortBy} sortOrder={customerSortOrder} column="total_orders" />
                      </th>
                      <th 
                        className="text-left p-3 font-medium cursor-pointer hover:bg-slate-200 select-none"
                        onClick={() => handleTableSort(setCustomerSortBy, setCustomerSortOrder, customerSortBy, "last_order")}
                      >
                        Last Order <TableSortIcon sortBy={customerSortBy} sortOrder={customerSortOrder} column="last_order" />
                      </th>
                      <th className="text-left p-3 font-medium">Top Product</th>
                      <th className="text-left p-3 font-medium">Category</th>
                      <th className="text-center p-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allCustomersLoading ? (
                      <tr>
                        <td colSpan={8} className="text-center p-8">
                          <RefreshCw className="w-6 h-6 animate-spin mx-auto text-blue-500" />
                        </td>
                      </tr>
                    ) : sortedAllCustomers.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center p-8 text-slate-500">No customers found</td>
                      </tr>
                    ) : sortedAllCustomers.map((customer, idx) => (
                      <tr key={idx} className="border-b hover:bg-slate-50">
                        <td className="p-3">
                          <Badge variant="outline" className="bg-blue-50 font-medium">
                            {customer.client_code}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-medium text-xs">
                              {customer.customer_name.charAt(0).toUpperCase()}
                            </div>
                            <span className="font-medium">{customer.customer_name}</span>
                            {customer.is_repeat && (
                              <Badge className="text-xs bg-green-100 text-green-700">Repeat</Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className="bg-slate-50">
                            <Globe className="w-3 h-3 mr-1" /> {customer.country || "-"}
                          </Badge>
                        </td>
                        <td className="p-3 text-center font-medium">{customer.total_orders}</td>
                        <td className="p-3">{customer.last_order_formatted || "-"}</td>
                        <td className="p-3">
                          {customer.top_products?.[0] && (
                            <div className="flex items-center gap-1 text-xs">
                              <Package className="w-3 h-3 text-slate-400" />
                              <span className="truncate max-w-[150px]">{customer.top_products[0].name}</span>
                              <Badge variant="secondary" className="text-xs">{customer.top_products[0].count}x</Badge>
                            </div>
                          )}
                        </td>
                        <td className="p-3">
                          {customer.categories?.[0] && (
                            <Badge variant="outline" className="text-xs">{customer.categories[0]}</Badge>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              title="View Partner Report"
                              onClick={() => handleOpenReport(customer.client_code)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-blue-600"
                              title="Schedule Follow-up"
                              onClick={() => { setSelectedCustomer(customer); setShowCustomerFollowUpModal(true); }}
                            >
                              <CalendarIcon className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-green-600"
                              title="Log Activity"
                              onClick={() => { setSelectedCustomer(customer); setShowCustomerActivityModal(true); }}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {allCustomersPagination.total > allCustomersPagination.limit && (
                <div className="flex items-center justify-between p-4 border-t">
                  <p className="text-sm text-slate-500">
                    Showing {((allCustomersPagination.page - 1) * allCustomersPagination.limit) + 1} to {Math.min(allCustomersPagination.page * allCustomersPagination.limit, allCustomersPagination.total)} of {allCustomersPagination.total}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={allCustomersPagination.page === 1}
                      onClick={() => setAllCustomersPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={allCustomersPagination.page * allCustomersPagination.limit >= allCustomersPagination.total}
                      onClick={() => setAllCustomersPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Customers Tab (Partners only - grouped by Partner Code) */}
        <TabsContent value="customers" className="space-y-4">
          {/* Customer Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-xs text-slate-500">Search Partner</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search partner code, customer name..."
                      value={customerFilters.search}
                      onChange={(e) => setCustomerFilters(prev => ({ ...prev, search: e.target.value }))}
                      className="pl-9"
                    />
                  </div>
                </div>
                
                <div className="w-48">
                  <Label className="text-xs text-slate-500">Country</Label>
                  <Select 
                    value={customerFilters.country} 
                    onValueChange={(v) => setCustomerFilters(prev => ({ ...prev, country: v === "all" ? "" : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Countries" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Countries</SelectItem>
                      {customerCountries.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchCustomerGroups}
                  disabled={customerLoading}
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${customerLoading ? "animate-spin" : ""}`} />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Customer Groups (Expandable by Partner) - PARTNERS ONLY */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Customers by Partner
                <Badge variant="outline" className="bg-blue-50 text-blue-700">Partners Only</Badge>
                <span className="text-sm font-normal text-slate-500">
                  (Click to expand and see customers)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {customerLoading ? (
                <div className="p-8 text-center">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto text-blue-500" />
                </div>
              ) : customerGroups.length === 0 ? (
                <div className="p-8 text-center text-slate-500">No partner data found</div>
              ) : (
                <div className="divide-y">
                  {customerGroups.map(group => (
                    <div key={group.client_code} className="border-l-4 border-l-blue-500">
                      {/* Group Header */}
                      <div 
                        className="p-4 hover:bg-slate-50 cursor-pointer flex items-center justify-between"
                        onClick={() => toggleGroup(group.client_code)}
                      >
                        <div className="flex items-center gap-4">
                          {expandedGroups[group.client_code] ? (
                            <ChevronDown className="w-5 h-5 text-blue-500" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          )}
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold">
                            {group.client_code.substring(0, 2)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-lg">{group.client_code}</span>
                              {group.client_name && (
                                <span className="text-slate-500">({group.client_name})</span>
                              )}
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">Partner</Badge>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" /> {group.customer_count} customers
                              </span>
                              <span className="flex items-center gap-1">
                                <Package className="w-3 h-3" /> {group.total_orders} orders
                              </span>
                              <span className="flex items-center gap-1">
                                <Globe className="w-3 h-3" /> {group.country_count} countries
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-semibold text-lg">{formatCurrency(group.total_value)}</p>
                            <p className="text-xs text-slate-500">Total Value</p>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleOpenReport(group.client_code); }}
                            className="gap-1"
                          >
                            <ExternalLink className="w-4 h-4" /> Open
                          </Button>
                        </div>
                      </div>
                      
                      {/* Expanded Customers List */}
                      {expandedGroups[group.client_code] && (
                        <div className="bg-slate-50 border-t">
                          {!groupCustomers[group.client_code] ? (
                            <div className="p-4 text-center">
                              <RefreshCw className="w-4 h-4 animate-spin mx-auto text-blue-500" />
                            </div>
                          ) : groupCustomers[group.client_code].length === 0 ? (
                            <div className="p-4 text-center text-slate-500">No customers found</div>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-slate-100">
                                    <th className="text-left p-3 font-medium">Customer Name</th>
                                    <th className="text-left p-3 font-medium">Country</th>
                                    <th className="text-center p-3 font-medium">Orders</th>
                                    <th className="text-right p-3 font-medium">Value</th>
                                    <th className="text-left p-3 font-medium">Last Order</th>
                                    <th className="text-left p-3 font-medium">Top Products</th>
                                    <th className="text-left p-3 font-medium">Categories</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {groupCustomers[group.client_code].map((customer, idx) => (
                                    <tr key={idx} className="border-b hover:bg-white transition-colors">
                                      <td className="p-3">
                                        <div className="flex items-center gap-2">
                                          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-medium text-xs">
                                            {customer.customer_name.charAt(0).toUpperCase()}
                                          </div>
                                          <div>
                                            <p className="font-medium">{customer.customer_name}</p>
                                            {customer.is_repeat && (
                                              <Badge className="text-xs bg-green-100 text-green-700">
                                                <Repeat className="w-3 h-3 mr-1" /> Repeat
                                              </Badge>
                                            )}
                                          </div>
                                        </div>
                                      </td>
                                      <td className="p-3">
                                        <Badge variant="outline" className="bg-blue-50">
                                          <Globe className="w-3 h-3 mr-1" /> {customer.country || "-"}
                                        </Badge>
                                      </td>
                                      <td className="p-3 text-center font-medium">{customer.total_orders}</td>
                                      <td className="p-3 text-right font-medium">{formatCurrency(customer.total_value)}</td>
                                      <td className="p-3">
                                        <span className="text-sm">{customer.last_order_formatted || "-"}</span>
                                      </td>
                                      <td className="p-3">
                                        <div className="space-y-1">
                                          {customer.top_products?.slice(0, 2).map((p, i) => (
                                            <div key={i} className="flex items-center gap-1 text-xs">
                                              <Package className="w-3 h-3 text-slate-400" />
                                              <span className="truncate max-w-[150px]">{p.name}</span>
                                              <Badge variant="secondary" className="text-xs">{p.count}x</Badge>
                                            </div>
                                          ))}
                                        </div>
                                      </td>
                                      <td className="p-3">
                                        <div className="flex flex-wrap gap-1">
                                          {customer.categories?.slice(0, 2).map((cat, i) => (
                                            <Badge key={i} variant="outline" className="text-xs">{cat}</Badge>
                                          ))}
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Clients Tab (Partners/DP Clients) */}
        {/* Clients/Agents Tab (DP Clients and Agents only) */}
        <TabsContent value="clients" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-xs text-slate-500">Search Client or Agent</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search client code, customer name..."
                      value={clientsAgentsFilters.search}
                      onChange={(e) => setClientsAgentsFilters(prev => ({ ...prev, search: e.target.value }))}
                      className="pl-9"
                    />
                  </div>
                </div>
                
                <div className="w-48">
                  <Label className="text-xs text-slate-500">Country</Label>
                  <Select 
                    value={clientsAgentsFilters.country} 
                    onValueChange={(v) => setClientsAgentsFilters(prev => ({ ...prev, country: v === "all" ? "" : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Countries" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Countries</SelectItem>
                      {availableCountries.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchClientsAgentsGroups}
                  disabled={clientsAgentsLoading}
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${clientsAgentsLoading ? "animate-spin" : ""}`} />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Clients/Agents Groups - DP CLIENTS AND AGENTS ONLY */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-green-600" />
                DP Clients & Agents
                <Badge variant="outline" className="bg-green-50 text-green-700">Clients + Agents Only</Badge>
                <span className="text-sm font-normal text-slate-500">
                  ({clientsAgentsSummary?.total_groups || 0} codes, {clientsAgentsSummary?.total_customers || 0} customers)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {clientsAgentsLoading ? (
                <div className="p-8 text-center">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto text-green-500" />
                </div>
              ) : clientsAgentsGroups.length === 0 ? (
                <div className="p-8 text-center text-slate-500">No DP Clients or Agents found</div>
              ) : (
                <div className="divide-y">
                  {clientsAgentsGroups.map(group => (
                    <div key={group.client_code} className={`border-l-4 ${group.role?.toLowerCase() === 'agent' ? 'border-l-purple-500' : 'border-l-green-500'}`}>
                      {/* Group Header */}
                      <div 
                        className="p-4 hover:bg-slate-50 cursor-pointer flex items-center justify-between"
                        onClick={() => toggleClientsAgentsGroup(group.client_code)}
                      >
                        <div className="flex items-center gap-4">
                          {expandedClientsAgentsGroups[group.client_code] ? (
                            <ChevronDown className={`w-5 h-5 ${group.role?.toLowerCase() === 'agent' ? 'text-purple-500' : 'text-green-500'}`} />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          )}
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${group.role?.toLowerCase() === 'agent' ? 'bg-gradient-to-br from-purple-500 to-purple-600' : 'bg-gradient-to-br from-green-500 to-green-600'}`}>
                            {group.client_code.substring(0, 2)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-lg">{group.client_code}</span>
                              {group.client_name && (
                                <span className="text-slate-500">({group.client_name})</span>
                              )}
                              <Badge 
                                variant="outline" 
                                className={`text-xs ${group.role?.toLowerCase() === 'agent' ? 'bg-purple-50 text-purple-700' : 'bg-green-50 text-green-700'}`}
                              >
                                {group.role?.toLowerCase() === 'agent' ? 'Agent' : 'DP Client'}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" /> {group.customer_count} customers
                              </span>
                              <span className="flex items-center gap-1">
                                <Package className="w-3 h-3" /> {group.total_orders} orders
                              </span>
                              <span className="flex items-center gap-1">
                                <Globe className="w-3 h-3" /> {group.country_count} countries
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-semibold text-lg">{formatCurrency(group.total_value)}</p>
                            <p className="text-xs text-slate-500">Total Value</p>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenTimeline(group.client_code);
                            }}
                          >
                            <History className="w-4 h-4 mr-1" />
                            Timeline
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenReport(group.client_code);
                            }}
                          >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            Open
                          </Button>
                        </div>
                      </div>
                      
                      {/* Expanded Customer List */}
                      {expandedClientsAgentsGroups[group.client_code] && (
                        <div className="bg-slate-50 p-4 border-t">
                          {!clientsAgentsGroupCustomers[group.client_code] ? (
                            <div className="text-center py-4">
                              <RefreshCw className="w-5 h-5 animate-spin mx-auto text-slate-400" />
                            </div>
                          ) : clientsAgentsGroupCustomers[group.client_code].length === 0 ? (
                            <p className="text-center text-slate-500 py-4">No customer data</p>
                          ) : (
                            <div className="space-y-2">
                              <p className="text-xs text-slate-500 mb-2">
                                Top {Math.min(10, clientsAgentsGroupCustomers[group.client_code].length)} customers by order count:
                              </p>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {clientsAgentsGroupCustomers[group.client_code].slice(0, 10).map((customer, idx) => (
                                  <div key={idx} className="bg-white rounded-lg p-3 border flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-medium text-xs">
                                      {customer.customer_name.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-sm truncate">{customer.customer_name}</p>
                                      <div className="flex items-center gap-2 text-xs text-slate-500">
                                        <span className="flex items-center gap-1">
                                          <Globe className="w-3 h-3" /> {customer.country || "-"}
                                        </span>
                                        <span className="flex items-center gap-1">
                                          <Package className="w-3 h-3" /> {customer.total_orders}
                                        </span>
                                        {customer.is_repeat && (
                                          <Badge className="text-xs bg-green-100 text-green-700 px-1 py-0">Repeat</Badge>
                                        )}
                                      </div>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedCustomer(customer);
                                        setShowActivityModal(true);
                                      }}
                                    >
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                              {clientsAgentsGroupCustomers[group.client_code].length > 10 && (
                                <p className="text-xs text-slate-400 text-center mt-2">
                                  +{clientsAgentsGroupCustomers[group.client_code].length - 10} more customers
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Follow-ups Tab */}
        <TabsContent value="follow-ups" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Overdue */}
            <Card className="border-l-4 border-l-red-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-5 h-5" /> Overdue ({dashboard?.follow_ups?.overdue || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {dashboard?.follow_ups?.overdue_list?.map((f, i) => (
                  <div key={i} className="p-3 bg-red-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{f.client_code}</span>
                      <Badge className="text-xs bg-red-100 text-red-700">{f.follow_up_date}</Badge>
                    </div>
                    <p className="text-xs text-slate-600 mt-1">{f.notes || f.follow_up_type}</p>
                    <div className="flex gap-1 mt-2">
                      <Button size="sm" variant="default" className="h-6 px-2 text-xs bg-green-600 hover:bg-green-700" onClick={() => openMarkDone(f)}>
                        <CheckCheck className="w-3 h-3 mr-1" /> Done
                      </Button>
                      <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => openReschedule(f)}>
                        <RotateCcw className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                )) || <p className="text-slate-500 text-sm">No overdue follow-ups</p>}
              </CardContent>
            </Card>

            {/* Due Today */}
            <Card className="border-l-4 border-l-yellow-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-yellow-600">
                  <Clock className="w-5 h-5" /> Due Today ({dashboard?.follow_ups?.due_today || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {dashboard?.follow_ups?.today_list?.map((f, i) => (
                  <div key={i} className="p-3 bg-yellow-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{f.client_code}</span>
                      <Badge className={`text-xs ${PRIORITY_CONFIG[f.priority]?.color}`}>{f.priority}</Badge>
                    </div>
                    <p className="text-xs text-slate-600 mt-1">{f.notes || f.follow_up_type}</p>
                    <div className="flex gap-1 mt-2">
                      <Button size="sm" variant="default" className="h-6 px-2 text-xs bg-green-600 hover:bg-green-700" onClick={() => openMarkDone(f)}>
                        <CheckCheck className="w-3 h-3 mr-1" /> Done
                      </Button>
                      <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => openReschedule(f)}>
                        <RotateCcw className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                )) || <p className="text-slate-500 text-sm">No follow-ups due today</p>}
              </CardContent>
            </Card>

            {/* Upcoming */}
            <Card className="border-l-4 border-l-green-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-green-600">
                  <CalendarIcon className="w-5 h-5" /> Upcoming ({dashboard?.follow_ups?.upcoming || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {dashboard?.follow_ups?.upcoming_list?.map((f, i) => (
                  <div key={i} className="p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{f.client_code}</span>
                      <Badge className="text-xs bg-green-100 text-green-700">{f.follow_up_date}</Badge>
                    </div>
                    <p className="text-xs text-slate-600 mt-1">{f.notes || f.follow_up_type}</p>
                    <div className="flex gap-1 mt-2">
                      <Button size="sm" variant="default" className="h-6 px-2 text-xs bg-green-600 hover:bg-green-700" onClick={() => openMarkDone(f)}>
                        <CheckCheck className="w-3 h-3 mr-1" /> Done
                      </Button>
                      <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => openReschedule(f)}>
                        <RotateCcw className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                )) || <p className="text-slate-500 text-sm">No upcoming follow-ups</p>}
              </CardContent>
            </Card>
          </div>

          {/* Templates & Automation Actions */}
          <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-purple-600" />
                  <span className="font-medium text-purple-800">Automation & Templates</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="border-green-300 text-green-700 hover:bg-green-100"
                    onClick={() => { fetchTemplates(); setShowTemplatesGallery(true); }}
                  >
                    <BookOpen className="w-4 h-4 mr-1" /> View Templates
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="border-purple-300 text-purple-700 hover:bg-purple-100"
                    onClick={() => { fetchTemplates(); setShowTemplateModal(true); }}
                  >
                    <Layers className="w-4 h-4 mr-1" /> Apply Template
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                    onClick={() => setShowRecurringModal(true)}
                  >
                    <Repeat className="w-4 h-4 mr-1" /> Create Recurring
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="border-orange-300 text-orange-700 hover:bg-orange-100"
                    onClick={() => downloadCRMTemplate("complete_crm")}
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-1" /> Download CRM Excel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actionable Follow-ups Table */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-600" />
                    All Pending Follow-ups
                    <Badge variant="outline" className="ml-2">{filteredSortedFollowUps.length}</Badge>
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => window.open('/crm/all-follow-ups', '_blank')}
                      className="border-purple-300 text-purple-700 hover:bg-purple-50"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" /> Open in New Tab
                    </Button>
                    <Button variant="outline" size="sm" onClick={fetchAllFollowUps}>
                      <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                    </Button>
                  </div>
                </div>
                {/* Search and Filter Row */}
                <div className="flex flex-wrap gap-3 items-center">
                  <div className="flex-1 min-w-[200px] relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search client, customer, notes..."
                      value={followUpSearch}
                      onChange={(e) => setFollowUpSearch(e.target.value)}
                      className="pl-9 h-9"
                    />
                  </div>
                  <Select value={followUpFilterType} onValueChange={setFollowUpFilterType}>
                    <SelectTrigger className="w-[140px] h-9">
                      <SelectValue placeholder="Filter by" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="partner">Partners</SelectItem>
                      <SelectItem value="client">DP Clients</SelectItem>
                      <SelectItem value="agent">Agents</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={followUpSortBy} onValueChange={setFollowUpSortBy}>
                    <SelectTrigger className="w-[130px] h-9">
                      <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="date">Date</SelectItem>
                      <SelectItem value="client">Client</SelectItem>
                      <SelectItem value="priority">Priority</SelectItem>
                      <SelectItem value="type">Type</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setFollowUpSortOrder(prev => prev === "asc" ? "desc" : "asc")}
                    className="h-9"
                  >
                    {followUpSortOrder === "asc" ? "↑ Asc" : "↓ Desc"}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="followups-action-table">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th 
                        className="text-left p-3 font-medium cursor-pointer hover:bg-slate-100"
                        onClick={() => { setFollowUpSortBy("client"); setFollowUpSortOrder(prev => prev === "asc" ? "desc" : "asc"); }}
                      >
                        Client/Customer {followUpSortBy === "client" && (followUpSortOrder === "asc" ? "↑" : "↓")}
                      </th>
                      <th 
                        className="text-left p-3 font-medium cursor-pointer hover:bg-slate-100"
                        onClick={() => { setFollowUpSortBy("type"); setFollowUpSortOrder(prev => prev === "asc" ? "desc" : "asc"); }}
                      >
                        Type {followUpSortBy === "type" && (followUpSortOrder === "asc" ? "↑" : "↓")}
                      </th>
                      <th 
                        className="text-left p-3 font-medium cursor-pointer hover:bg-slate-100"
                        onClick={() => { setFollowUpSortBy("date"); setFollowUpSortOrder(prev => prev === "asc" ? "desc" : "asc"); }}
                      >
                        Date {followUpSortBy === "date" && (followUpSortOrder === "asc" ? "↑" : "↓")}
                      </th>
                      <th 
                        className="text-center p-3 font-medium cursor-pointer hover:bg-slate-100"
                        onClick={() => { setFollowUpSortBy("priority"); setFollowUpSortOrder(prev => prev === "asc" ? "desc" : "asc"); }}
                      >
                        Priority {followUpSortBy === "priority" && (followUpSortOrder === "asc" ? "↑" : "↓")}
                      </th>
                      <th className="text-left p-3 font-medium">Assigned</th>
                      <th className="text-left p-3 font-medium">Notes</th>
                      <th className="text-center p-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSortedFollowUps.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center p-8 text-slate-500">
                          {followUpSearch ? "No follow-ups match your search" : "No pending follow-ups"}
                        </td>
                      </tr>
                    ) : filteredSortedFollowUps.map((f, idx) => {
                      const isOverdue = f.follow_up_date < new Date().toISOString().split('T')[0];
                      return (
                        <tr key={f.id || idx} className={`border-b hover:bg-slate-50 ${isOverdue ? 'bg-red-50/50' : ''}`}>
                          <td className="p-3">
                            <div className="flex flex-col">
                              <span className="font-medium">{f.client_code}</span>
                              {f.customer_name && (
                                <span className="text-xs text-slate-500">{f.customer_name}</span>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            <Badge variant="outline" className="text-xs capitalize">
                              {f.follow_up_type?.replace('_', ' ')}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <div className="flex flex-col">
                              <span className={isOverdue ? 'text-red-600 font-medium' : ''}>{f.follow_up_date}</span>
                              {isOverdue && <span className="text-xs text-red-500">Overdue</span>}
                            </div>
                          </td>
                          <td className="p-3 text-center">
                            <Badge className={`text-xs ${PRIORITY_CONFIG[f.priority]?.color}`}>
                              {f.priority}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <span className="text-sm text-slate-600">
                              {f.assigned_to || '-'}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className="text-sm text-slate-600 truncate max-w-[200px] block">
                              {f.notes || '-'}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                size="sm"
                                variant="default"
                                className="h-7 px-2 bg-green-600 hover:bg-green-700"
                                onClick={() => openMarkDone(f)}
                                title="Mark Done"
                              >
                                <CheckCheck className="w-4 h-4 mr-1" /> Done
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2"
                                onClick={() => openReschedule(f)}
                                title="Reschedule"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-blue-600"
                                onClick={() => openTimeline(f.client_code)}
                                title="View History"
                              >
                                <History className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-purple-600"
                                onClick={() => openAssignModal(f)}
                                title="Assign to Team"
                              >
                                <UserPlus className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-red-500 hover:text-red-700"
                                onClick={() => handleCancelFollowUp(f)}
                                title="Cancel"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Recently Completed Follow-ups */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                Recently Completed
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {completedFollowUps.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-4">No completed follow-ups yet</p>
                ) : completedFollowUps.map((f, i) => {
                  const OutcomeIcon = OUTCOME_CONFIG[f.outcome]?.icon || CheckCircle2;
                  return (
                    <div key={f.id || i} className="p-3 bg-slate-50 rounded-lg flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${OUTCOME_CONFIG[f.outcome]?.color || 'bg-gray-100'}`}>
                          <OutcomeIcon className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{f.client_code}</span>
                            {f.customer_name && <span className="text-xs text-slate-500">({f.customer_name})</span>}
                          </div>
                          <p className="text-xs text-slate-600">{f.completion_notes || f.notes || f.follow_up_type}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={`text-xs ${OUTCOME_CONFIG[f.outcome]?.color || 'bg-gray-100 text-gray-700'}`}>
                          {OUTCOME_CONFIG[f.outcome]?.label || f.outcome}
                        </Badge>
                        <p className="text-xs text-slate-400 mt-1">{f.completed_at?.split('T')[0]}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          {/* Analytics Search Bar */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-3 items-center">
                <div className="flex-1 min-w-[250px] relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search analytics by partner, client, agent, customer..."
                    value={analyticsSearch}
                    onChange={(e) => setAnalyticsSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select defaultValue="all">
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="View by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="partners">Partners Only</SelectItem>
                    <SelectItem value="clients">DP Clients Only</SelectItem>
                    <SelectItem value="agents">Agents Only</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={() => { fetchDashboard(); fetchFollowUpAnalytics(); }}>
                  <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Client Status Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Client Status Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {statusPieData.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                {/* Clickable Dormant Info */}
                {summary?.dormant > 0 && (
                  <div 
                    className="mt-3 p-3 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-colors"
                    onClick={fetchDormantClients}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                        <span className="font-medium text-red-700">{summary.dormant} Dormant Clients (45+ days)</span>
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-600">
                        View List <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top Countries - Changed to Parcels */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Top Countries by Parcels</CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => window.open('/reports', '_blank')}
                >
                  View More <ExternalLink className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dashboard?.top_countries?.slice(0, 8) || []} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="country" type="category" width={100} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(value) => [`${value} parcels`, 'Parcels']} />
                      <Bar dataKey="orders" fill="#3b82f6" radius={[0, 4, 4, 0]} name="Parcels" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activities */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5" /> Recent Activities
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboard?.recent_activities?.map((activity, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <MessageSquare className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{activity.client_code}</span>
                        <Badge variant="outline" className="text-xs">{activity.activity_type}</Badge>
                      </div>
                      <p className="text-sm text-slate-600">{activity.description}</p>
                      <p className="text-xs text-slate-400 mt-1">{activity.created_at?.split("T")[0]}</p>
                    </div>
                  </div>
                )) || <p className="text-slate-500">No recent activities</p>}
              </div>
            </CardContent>
          </Card>

          {/* Follow-up Analytics */}
          {followUpAnalytics && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-sm">Total Follow-ups</p>
                        <p className="text-3xl font-bold">{followUpAnalytics.summary?.total_follow_ups || 0}</p>
                      </div>
                      <Target className="w-10 h-10 text-blue-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100 text-sm">Completion Rate</p>
                        <p className="text-3xl font-bold">{followUpAnalytics.summary?.completion_rate || 0}%</p>
                      </div>
                      <Percent className="w-10 h-10 text-green-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-emerald-100 text-sm">Conversion Rate</p>
                        <p className="text-3xl font-bold">{followUpAnalytics.summary?.conversion_rate || 0}%</p>
                      </div>
                      <Award className="w-10 h-10 text-emerald-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white border-0">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-red-100 text-sm">Overdue</p>
                        <p className="text-3xl font-bold">{followUpAnalytics.summary?.overdue || 0}</p>
                      </div>
                      <AlertTriangle className="w-10 h-10 text-red-200" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Outcome Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Follow-up Outcomes Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {followUpAnalytics.by_outcome?.map((item, i) => {
                      const config = OUTCOME_CONFIG[item.outcome];
                      const OutcomeIcon = config?.icon || CheckCircle2;
                      return (
                        <div key={i} className={`p-3 rounded-lg ${config?.color || 'bg-gray-100'}`}>
                          <div className="flex items-center gap-2">
                            <OutcomeIcon className="w-4 h-4" />
                            <span className="text-sm font-medium">{config?.label || item.outcome}</span>
                          </div>
                          <p className="text-2xl font-bold mt-1">{item.count}</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Client Detail Modal */}
      <Dialog open={showClientDetail} onOpenChange={setShowClientDetail}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold">
                {clientDetail?.client_code?.substring(0, 2)}
              </div>
              <div>
                <p className="text-xl">{clientDetail?.client_code}</p>
                <p className="text-sm text-slate-500 font-normal">{clientDetail?.ledger?.name}</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {clientDetail && (
            <div className="space-y-6">
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                {clientDetail.ledger?.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span className="text-sm">{clientDetail.ledger.email}</span>
                  </div>
                )}
                {clientDetail.ledger?.phone_number && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span className="text-sm">{clientDetail.ledger.phone_number}</span>
                  </div>
                )}
              </div>

              {/* Monthly Trend Chart */}
              {clientDetail.monthly_trend?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Monthly Order Trend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={clientDetail.monthly_trend}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip />
                          <Bar dataKey="orders" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recent Orders */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Recent Orders</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {clientDetail.orders?.slice(0, 10).map((order, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline">{formatDMY(order.order_date)}</Badge>
                          <span>{order.detected_country}</span>
                        </div>
                        <span className="font-medium">{formatCurrency((order.medicine_cost || 0) + (order.shipping_cost || 0))}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Activities Timeline */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Activity Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {clientDetail.activities?.length > 0 ? clientDetail.activities.map((activity, i) => (
                      <div key={i} className="flex items-start gap-2 p-2 bg-slate-50 rounded">
                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <MessageSquare className="w-3 h-3 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm">{activity.description}</p>
                          <p className="text-xs text-slate-400">{activity.created_at?.split("T")[0]}</p>
                        </div>
                      </div>
                    )) : (
                      <p className="text-slate-500 text-sm">No activities logged yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button onClick={() => { setShowFollowUpModal(true); }} className="gap-2">
                  <CalendarIcon className="w-4 h-4" /> Schedule Follow-up
                </Button>
                <Button variant="outline" onClick={() => { setShowActivityModal(true); }} className="gap-2">
                  <Plus className="w-4 h-4" /> Log Activity
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Follow-up Modal */}
      <Dialog open={showFollowUpModal} onOpenChange={setShowFollowUpModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-blue-500" />
              Schedule Follow-up for {selectedClient?.client_code}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Follow-up Date *</Label>
              <DMYDateInput
  value={followUpForm.follow_up_date}
                onChange={(e) => setFollowUpForm(prev => ({ ...prev, follow_up_date: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={followUpForm.follow_up_type} onValueChange={(v) => setFollowUpForm(prev => ({ ...prev, follow_up_type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FOLLOW_UP_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={followUpForm.priority} onValueChange={(v) => setFollowUpForm(prev => ({ ...prev, priority: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={followUpForm.notes}
                onChange={(e) => setFollowUpForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="What do you want to discuss?"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFollowUpModal(false)}>Cancel</Button>
            <Button onClick={handleCreateFollowUp}>Schedule Follow-up</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity Modal */}
      <Dialog open={showActivityModal} onOpenChange={setShowActivityModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-green-500" />
              Log Activity for {selectedClient?.client_code}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Activity Type</Label>
              <Select value={activityForm.activity_type} onValueChange={(v) => setActivityForm(prev => ({ ...prev, activity_type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Phone Call</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="note">Note</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={activityForm.description}
                onChange={(e) => setActivityForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What happened during this interaction?"
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Outcome</Label>
              <Select value={activityForm.outcome} onValueChange={(v) => setActivityForm(prev => ({ ...prev, outcome: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="positive">Positive</SelectItem>
                  <SelectItem value="neutral">Neutral</SelectItem>
                  <SelectItem value="negative">Negative</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Next Action</Label>
                <Input
                  value={activityForm.next_action}
                  onChange={(e) => setActivityForm(prev => ({ ...prev, next_action: e.target.value }))}
                  placeholder="What's the next step?"
                />
              </div>
              <div className="space-y-2">
                <Label>Next Action Date</Label>
                <DMYDateInput
  value={activityForm.next_action_date}
                  onChange={(e) => setActivityForm(prev => ({ ...prev, next_action_date: e.target.value }))}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActivityModal(false)}>Cancel</Button>
            <Button onClick={handleLogActivity}>Log Activity</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Follow-up Modal (for All Customers tab) */}
      <Dialog open={showCustomerFollowUpModal} onOpenChange={setShowCustomerFollowUpModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-blue-500" />
              Schedule Follow-up
            </DialogTitle>
            {selectedCustomer && (
              <p className="text-sm text-slate-500">
                Customer: <strong>{selectedCustomer.customer_name}</strong> ({selectedCustomer.client_code})
              </p>
            )}
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Follow-up Date *</Label>
              <DMYDateInput
  value={followUpForm.follow_up_date}
                onChange={(e) => setFollowUpForm(prev => ({ ...prev, follow_up_date: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={followUpForm.follow_up_type} onValueChange={(v) => setFollowUpForm(prev => ({ ...prev, follow_up_type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FOLLOW_UP_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={followUpForm.priority} onValueChange={(v) => setFollowUpForm(prev => ({ ...prev, priority: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={followUpForm.notes}
                onChange={(e) => setFollowUpForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="What do you want to discuss?"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomerFollowUpModal(false)}>Cancel</Button>
            <Button onClick={handleCreateCustomerFollowUp}>Schedule Follow-up</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Activity Modal (for All Customers tab) */}
      <Dialog open={showCustomerActivityModal} onOpenChange={setShowCustomerActivityModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-green-500" />
              Log Activity
            </DialogTitle>
            {selectedCustomer && (
              <p className="text-sm text-slate-500">
                Customer: <strong>{selectedCustomer.customer_name}</strong> ({selectedCustomer.client_code})
              </p>
            )}
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Activity Type</Label>
              <Select value={activityForm.activity_type} onValueChange={(v) => setActivityForm(prev => ({ ...prev, activity_type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Phone Call</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="note">Note</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={activityForm.description}
                onChange={(e) => setActivityForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What happened during this interaction?"
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Outcome</Label>
              <Select value={activityForm.outcome} onValueChange={(v) => setActivityForm(prev => ({ ...prev, outcome: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="positive">Positive</SelectItem>
                  <SelectItem value="neutral">Neutral</SelectItem>
                  <SelectItem value="negative">Negative</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Next Action</Label>
                <Input
                  value={activityForm.next_action}
                  onChange={(e) => setActivityForm(prev => ({ ...prev, next_action: e.target.value }))}
                  placeholder="What's the next step?"
                />
              </div>
              <div className="space-y-2">
                <Label>Next Action Date</Label>
                <DMYDateInput
  value={activityForm.next_action_date}
                  onChange={(e) => setActivityForm(prev => ({ ...prev, next_action_date: e.target.value }))}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomerActivityModal(false)}>Cancel</Button>
            <Button onClick={handleLogCustomerActivity}>Log Activity</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Done Modal */}
      <Dialog open={showMarkDoneModal} onOpenChange={setShowMarkDoneModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCheck className="w-5 h-5 text-green-500" />
              Mark Follow-up as Done
            </DialogTitle>
            {selectedFollowUp && (
              <p className="text-sm text-slate-500">
                {selectedFollowUp.client_code} {selectedFollowUp.customer_name && `- ${selectedFollowUp.customer_name}`}
              </p>
            )}
          </DialogHeader>
          
          <form onSubmit={handleMarkDone} className="space-y-4">
            <div className="space-y-2">
              <Label>Outcome *</Label>
              <Select value={markDoneForm.outcome} onValueChange={(v) => setMarkDoneForm(prev => ({ ...prev, outcome: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(OUTCOME_CONFIG).map(([value, config]) => (
                    <SelectItem key={value} value={value}>
                      <span className="flex items-center gap-2">
                        <config.icon className="w-4 h-4" />
                        {config.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={markDoneForm.notes}
                onChange={(e) => setMarkDoneForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="What was discussed? Any important details?"
                rows={3}
              />
            </div>
            
            {/* Auto-prompt for next follow-up */}
            {OUTCOME_CONFIG[markDoneForm.outcome]?.suggestsNext && (
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <input 
                      type="checkbox"
                      id="create_next"
                      checked={markDoneForm.create_next}
                      onChange={(e) => setMarkDoneForm(prev => ({ ...prev, create_next: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="create_next" className="text-sm font-medium cursor-pointer">
                      Schedule next follow-up
                    </Label>
                  </div>
                  
                  {markDoneForm.create_next && (
                    <div className="space-y-3 mt-3 pt-3 border-t border-blue-200">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Date *</Label>
                          <DMYDateInput
  value={markDoneForm.next_follow_up_date}
                            onChange={(e) => setMarkDoneForm(prev => ({ ...prev, next_follow_up_date: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Type</Label>
                          <Select value={markDoneForm.next_follow_up_type} onValueChange={(v) => setMarkDoneForm(prev => ({ ...prev, next_follow_up_type: v }))}>
                            <SelectTrigger className="h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {FOLLOW_UP_TYPES.map(t => (
                                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Notes for next follow-up</Label>
                        <Input
                          value={markDoneForm.next_follow_up_notes}
                          onChange={(e) => setMarkDoneForm(prev => ({ ...prev, next_follow_up_notes: e.target.value }))}
                          placeholder="What to discuss next?"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Priority</Label>
                        <Select value={markDoneForm.next_follow_up_priority} onValueChange={(v) => setMarkDoneForm(prev => ({ ...prev, next_follow_up_priority: v }))}>
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowMarkDoneModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700">
                <CheckCheck className="w-4 h-4 mr-1" /> Mark as Done
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reschedule Modal */}
      <Dialog open={showRescheduleModal} onOpenChange={setShowRescheduleModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-blue-500" />
              Reschedule Follow-up
            </DialogTitle>
            {selectedFollowUp && (
              <p className="text-sm text-slate-500">
                Current date: <strong>{selectedFollowUp.follow_up_date}</strong>
              </p>
            )}
          </DialogHeader>
          
          <form onSubmit={handleReschedule} className="space-y-4">
            <div className="space-y-2">
              <Label>New Date *</Label>
              <DMYDateInput
  value={rescheduleForm.new_date}
                onChange={(e) => setRescheduleForm(prev => ({ ...prev, new_date: e.target.value }))}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea
                value={rescheduleForm.reason}
                onChange={(e) => setRescheduleForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Why is this being rescheduled?"
                rows={2}
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowRescheduleModal(false)}>Cancel</Button>
              <Button type="submit">Reschedule</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Timeline Modal */}
      <Dialog open={showTimelineModal} onOpenChange={setShowTimelineModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-blue-500" />
              Client Timeline & History
            </DialogTitle>
            {timelineStats && (
              <div className="flex gap-4 mt-2">
                <Badge variant="outline">{timelineStats.total_follow_ups} Follow-ups</Badge>
                <Badge variant="outline" className="bg-green-50">{timelineStats.completed_follow_ups} Completed</Badge>
                <Badge variant="outline" className="bg-yellow-50">{timelineStats.pending_follow_ups} Pending</Badge>
              </div>
            )}
          </DialogHeader>
          
          <div className="space-y-3 mt-4">
            {timeline.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No history found for this client</p>
            ) : (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200"></div>
                
                {timeline.map((item, i) => {
                  const isFollowUp = item.type === "follow_up";
                  const isCompleted = item.status === "completed";
                  const OutcomeIcon = isFollowUp && item.outcome ? (OUTCOME_CONFIG[item.outcome]?.icon || CheckCircle2) : (isFollowUp ? Bell : MessageSquare);
                  
                  return (
                    <div key={item.id || i} className="relative pl-10 pb-4">
                      {/* Timeline dot */}
                      <div className={`absolute left-2 w-5 h-5 rounded-full flex items-center justify-center ${
                        isCompleted ? 'bg-green-100' : 
                        item.status === "pending" ? 'bg-yellow-100' :
                        item.status === "cancelled" ? 'bg-red-100' :
                        'bg-blue-100'
                      }`}>
                        <OutcomeIcon className={`w-3 h-3 ${
                          isCompleted ? 'text-green-600' :
                          item.status === "pending" ? 'text-yellow-600' :
                          item.status === "cancelled" ? 'text-red-600' :
                          'text-blue-600'
                        }`} />
                      </div>
                      
                      <div className="bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs capitalize">
                              {isFollowUp ? item.follow_up_type?.replace('_', ' ') : item.activity_type?.replace('_', ' ')}
                            </Badge>
                            {isFollowUp && item.status && (
                              <Badge className={`text-xs ${
                                item.status === "completed" ? "bg-green-100 text-green-700" :
                                item.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                                item.status === "cancelled" ? "bg-red-100 text-red-700" :
                                "bg-gray-100 text-gray-700"
                              }`}>
                                {item.status}
                              </Badge>
                            )}
                            {item.outcome && (
                              <Badge className={`text-xs ${OUTCOME_CONFIG[item.outcome]?.color || 'bg-gray-100'}`}>
                                {OUTCOME_CONFIG[item.outcome]?.label || item.outcome}
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-slate-400">{item.date?.split('T')[0]}</span>
                        </div>
                        
                        {isFollowUp ? (
                          <>
                            {item.follow_up_date && (
                              <p className="text-sm text-slate-600">Scheduled for: {item.follow_up_date}</p>
                            )}
                            {item.notes && <p className="text-sm text-slate-600 mt-1">{item.notes}</p>}
                            {item.completion_notes && (
                              <p className="text-sm text-slate-600 mt-1 italic">Completion: {item.completion_notes}</p>
                            )}
                          </>
                        ) : (
                          <p className="text-sm text-slate-600">{item.description}</p>
                        )}
                        
                        {item.created_by && (
                          <p className="text-xs text-slate-400 mt-2">By: {item.created_by}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTimelineModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply Template Modal */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-purple-500" />
              Apply Follow-up Template
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleApplyTemplate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client Code *</Label>
                <Select 
                  value={templateForm.client_code} 
                  onValueChange={(v) => setTemplateForm(prev => ({ ...prev, client_code: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select client..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableClientCodes.map(code => (
                      <SelectItem key={code} value={code}>{code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <DMYDateInput
  value={templateForm.start_date}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, start_date: e.target.value }))}
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Select Template *</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto">
                {templates.map(template => (
                  <div
                    key={template.id}
                    onClick={() => setSelectedTemplate(template)}
                    className={`p-3 border rounded-lg cursor-pointer transition-all ${
                      selectedTemplate?.id === template.id 
                        ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200' 
                        : 'hover:border-slate-400'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{template.name}</span>
                      <Badge variant="outline" className="text-xs">{template.steps_count} steps</Badge>
                    </div>
                    <p className="text-xs text-slate-500">{template.description}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Template Preview */}
            {selectedTemplate && (
              <Card className="bg-slate-50">
                <CardHeader className="pb-2 pt-3">
                  <CardTitle className="text-sm">Template Steps Preview</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  {selectedTemplate.steps.map((step, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs p-2 bg-white rounded">
                      <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-medium">
                        {i + 1}
                      </span>
                      <Badge variant="outline" className="text-xs capitalize">{step.type}</Badge>
                      <span className="text-slate-500">Day +{step.day_offset}</span>
                      <span className="text-slate-600 truncate flex-1">{step.notes}</span>
                      <Badge className={`text-xs ${PRIORITY_CONFIG[step.priority]?.color}`}>
                        {step.priority}
                      </Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowTemplateModal(false)}>Cancel</Button>
              <Button type="submit" disabled={!selectedTemplate}>
                <Play className="w-4 h-4 mr-1" /> Apply Template
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Templates Gallery Modal - View All Available Templates */}
      <Dialog open={showTemplatesGallery} onOpenChange={setShowTemplatesGallery}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-green-500" />
              Available Follow-up Templates
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {templates.length === 0 ? (
              <div className="text-center py-8 text-slate-500">Loading templates...</div>
            ) : (
              templates.map((template) => (
                <Card key={template.id} className="border-l-4 border-l-purple-500">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                          <Layers className="w-4 h-4 text-purple-500" />
                          {template.name}
                        </h3>
                        <p className="text-sm text-slate-600 mt-1">{template.description}</p>
                      </div>
                      <Badge variant="outline" className="bg-purple-50 text-purple-700">
                        {template.steps_count} Steps
                      </Badge>
                    </div>
                    
                    {/* Template Steps */}
                    <div className="bg-slate-50 rounded-lg p-3 mt-3">
                      <p className="text-xs text-slate-500 mb-2 font-medium">FOLLOW-UP SEQUENCE:</p>
                      <div className="space-y-2">
                        {template.steps?.map((step, idx) => (
                          <div key={idx} className="flex items-center gap-3 text-sm">
                            <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-bold">
                              {idx + 1}
                            </div>
                            <Badge variant="outline" className={`text-xs ${
                              step.type === 'call' ? 'bg-green-50 text-green-700' :
                              step.type === 'email' ? 'bg-blue-50 text-blue-700' :
                              step.type === 'whatsapp' ? 'bg-emerald-50 text-emerald-700' :
                              step.type === 'meeting' ? 'bg-orange-50 text-orange-700' :
                              'bg-slate-50 text-slate-700'
                            }`}>
                              {step.type === 'call' && <Phone className="w-3 h-3 mr-1" />}
                              {step.type === 'email' && <Mail className="w-3 h-3 mr-1" />}
                              {step.type === 'whatsapp' && <MessageSquare className="w-3 h-3 mr-1" />}
                              {step.type === 'meeting' && <Video className="w-3 h-3 mr-1" />}
                              {step.type}
                            </Badge>
                            <span className="text-slate-600">Day {step.day_offset}:</span>
                            <span className="flex-1">{step.notes}</span>
                            <Badge variant="outline" className={`text-xs ${
                              step.priority === 'urgent' ? 'bg-red-50 text-red-700' :
                              step.priority === 'high' ? 'bg-orange-50 text-orange-700' :
                              step.priority === 'medium' ? 'bg-yellow-50 text-yellow-700' :
                              'bg-slate-50 text-slate-600'
                            }`}>
                              {step.priority}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="mt-3 flex justify-end">
                      <Button 
                        size="sm" 
                        onClick={() => {
                          setSelectedTemplate(template);
                          setShowTemplatesGallery(false);
                          setShowTemplateModal(true);
                        }}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        <Play className="w-4 h-4 mr-1" /> Apply This Template
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
          
          {/* Download CRM Templates Section */}
          <div className="border-t pt-4 mt-4">
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4 text-orange-500" />
              Download Excel Templates for Partners
            </h4>
            <p className="text-sm text-slate-600 mb-3">
              Share these templates with your partners so they can maintain customer data in Excel.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => downloadCRMTemplate("customer_tracker")}
                className="justify-start"
              >
                <Users className="w-4 h-4 mr-2 text-blue-500" />
                Customer Tracker
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => downloadCRMTemplate("follow_up_log")}
                className="justify-start"
              >
                <Bell className="w-4 h-4 mr-2 text-green-500" />
                Follow-up Log
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => downloadCRMTemplate("order_tracker")}
                className="justify-start"
              >
                <Package className="w-4 h-4 mr-2 text-purple-500" />
                Order Tracker
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => downloadCRMTemplate("payment_tracker")}
                className="justify-start"
              >
                <DollarSign className="w-4 h-4 mr-2 text-emerald-500" />
                Payment Tracker
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => downloadCRMTemplate("complete_crm")}
                className="justify-start col-span-2 md:col-span-1 bg-orange-50 border-orange-300 hover:bg-orange-100"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2 text-orange-600" />
                Complete CRM Package
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplatesGallery(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Recurring Follow-up Modal */}
      <Dialog open={showRecurringModal} onOpenChange={setShowRecurringModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Repeat className="w-5 h-5 text-blue-500" />
              Create Recurring Follow-up
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateRecurring} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client Code *</Label>
                <Select 
                  value={recurringForm.client_code} 
                  onValueChange={(v) => setRecurringForm(prev => ({ ...prev, client_code: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select client..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableClientCodes.map(code => (
                      <SelectItem key={code} value={code}>{code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Frequency *</Label>
                <Select 
                  value={recurringForm.frequency} 
                  onValueChange={(v) => setRecurringForm(prev => ({ ...prev, frequency: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Bi-weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Follow-up Type</Label>
                <Select 
                  value={recurringForm.follow_up_type} 
                  onValueChange={(v) => setRecurringForm(prev => ({ ...prev, follow_up_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FOLLOW_UP_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select 
                  value={recurringForm.priority} 
                  onValueChange={(v) => setRecurringForm(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <DMYDateInput
  value={recurringForm.start_date}
                  onChange={(e) => setRecurringForm(prev => ({ ...prev, start_date: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>End Date (Optional)</Label>
                <DMYDateInput
  value={recurringForm.end_date}
                  onChange={(e) => setRecurringForm(prev => ({ ...prev, end_date: e.target.value }))}
                  placeholder="Default: 3 months"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={recurringForm.notes}
                onChange={(e) => setRecurringForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="What to discuss on each follow-up?"
                rows={2}
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowRecurringModal(false)}>Cancel</Button>
              <Button type="submit">
                <Repeat className="w-4 h-4 mr-1" /> Create Recurring
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Follow-up Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-green-500" />
              Assign Follow-up
            </DialogTitle>
            {selectedForAssign && (
              <p className="text-sm text-slate-500">
                {selectedForAssign.client_code} - {selectedForAssign.follow_up_type} on {selectedForAssign.follow_up_date}
              </p>
            )}
          </DialogHeader>
          
          <form onSubmit={handleAssignFollowUp} className="space-y-4">
            <div className="space-y-2">
              <Label>Assign To *</Label>
              <Input
                value={assignForm.assigned_to}
                onChange={(e) => setAssignForm(prev => ({ ...prev, assigned_to: e.target.value }))}
                placeholder="Enter team member name"
                required
              />
            </div>
            
            {/* Team Workload Preview */}
            {teamWorkload.length > 0 && (
              <Card className="bg-slate-50">
                <CardHeader className="pb-2 pt-3">
                  <CardTitle className="text-sm">Team Workload</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {teamWorkload.slice(0, 5).map((member, i) => (
                    <div 
                      key={i} 
                      onClick={() => setAssignForm(prev => ({ ...prev, assigned_to: member.user }))}
                      className="flex items-center justify-between p-2 bg-white rounded cursor-pointer hover:bg-slate-100"
                    >
                      <span className="font-medium text-sm">{member.user}</span>
                      <div className="flex items-center gap-2 text-xs">
                        <Badge variant="outline">{member.total_pending} pending</Badge>
                        {member.overdue > 0 && (
                          <Badge className="bg-red-100 text-red-700">{member.overdue} overdue</Badge>
                        )}
                        {member.urgent > 0 && (
                          <Badge className="bg-orange-100 text-orange-700">{member.urgent} urgent</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
            
            <div className="space-y-2">
              <Label>Handoff Notes</Label>
              <Textarea
                value={assignForm.handoff_notes}
                onChange={(e) => setAssignForm(prev => ({ ...prev, handoff_notes: e.target.value }))}
                placeholder="Any context for the new assignee?"
                rows={2}
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAssignModal(false)}>Cancel</Button>
              <Button type="submit">
                <UserPlus className="w-4 h-4 mr-1" /> Assign
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dormant Clients Modal */}
      <Dialog open={showDormantModal} onOpenChange={setShowDormantModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Dormant Clients (45+ Days Inactive)
              <Badge variant="outline" className="ml-2 bg-red-50 text-red-700">{dormantClients.length}</Badge>
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            <p className="text-sm text-slate-600 mb-4">
              These clients haven't placed an order in the last 45+ days. Consider reaching out with a re-engagement campaign.
            </p>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-3 font-medium">#</th>
                    <th className="text-left p-3 font-medium">Client Code</th>
                    <th className="text-left p-3 font-medium">Name</th>
                    <th className="text-right p-3 font-medium">Total Orders</th>
                    <th className="text-right p-3 font-medium">Total Revenue</th>
                    <th className="text-left p-3 font-medium">Last Order</th>
                    <th className="text-center p-3 font-medium">Days Inactive</th>
                    <th className="text-center p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dormantClients.map((client, idx) => (
                    <tr key={client.client_code} className="border-b hover:bg-slate-50">
                      <td className="p-3 text-slate-500">{idx + 1}</td>
                      <td className="p-3 font-medium">{client.client_code}</td>
                      <td className="p-3">{client.name || "-"}</td>
                      <td className="p-3 text-right">{client.total_orders}</td>
                      <td className="p-3 text-right">{formatCurrency(client.total_revenue)}</td>
                      <td className="p-3">{client.last_order_formatted || "-"}</td>
                      <td className="p-3 text-center">
                        <Badge variant="outline" className="bg-red-50 text-red-700">
                          {client.days_since_order} days
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setSelectedClient(client);
                              setShowFollowUpModal(true);
                              setShowDormantModal(false);
                            }}
                          >
                            <Bell className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleOpenTimeline(client.client_code)}
                          >
                            <History className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDormantModal(false)}>Close</Button>
            <Button 
              onClick={() => {
                // Apply re-engagement template to all dormant clients
                toast.info("Re-engagement campaign feature coming soon!");
              }}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Zap className="w-4 h-4 mr-1" /> Start Re-engagement Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CRMPage;
